/**
 * StatusTimeline Component
 * Visual timeline showing order status progression
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Circle, Clock } from 'lucide-react';
import type { OrderStatus, StatusTimelineEntry } from '@/types/order';

interface StatusTimelineProps {
  timeline: StatusTimelineEntry[];
  currentStatus: OrderStatus;
}

/**
 * Order status flow - matches backend OrderStatus enum exactly
 * ORDER_INITIALIZED → PENDING → CONFIRMED → READY (Payment) → TRANSPORT_REQUESTED → IN_TRANSIT → DELIVERED → AWAITING_CUSTOMER_APPROVAL → COMPLETED
 */
const STATUS_FLOW: OrderStatus[] = [
  'ORDER_INITIALIZED',
  'PENDING',
  'CONFIRMED',
  'READY',               // Payment Received - comes after confirmation
  'TRANSPORT_REQUESTED', // Transport has been requested for delivery
  'IN_TRANSIT',
  'DELIVERED',
  'AWAITING_CUSTOMER_APPROVAL', // Customer must approve delivery before completion
  'COMPLETED',
];

/**
 * Human-readable labels for order statuses
 */
const STATUS_LABELS: Record<OrderStatus, string> = {
  ORDER_INITIALIZED: 'Order Created',
  PENDING: 'Pending Confirmation',
  CONFIRMED: 'Order Confirmed',
  READY: 'Payment Received',
  TRANSPORT_REQUESTED: 'Transport Requested',
  IN_PROGRESS: 'Being Prepared',
  IN_TRANSIT: 'In Transit',
  DELIVERED: 'Delivered',
  AWAITING_CUSTOMER_APPROVAL: 'Awaiting Customer Approval',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/**
 * Format date and time
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function StatusTimeline({ timeline, currentStatus }: StatusTimelineProps) {
  // Get the index of current status in the flow
  const currentStatusIndex = STATUS_FLOW.indexOf(currentStatus);

  /**
   * Check if status has been completed (includes current status - shows green tick)
   * Current status and all before it should show as completed (green tick)
   */
  function isStatusCompleted(status: OrderStatus): boolean {
    const statusIndex = STATUS_FLOW.indexOf(status);
    // A status is completed if it's at or before the current status in the flow
    return statusIndex >= 0 && statusIndex <= currentStatusIndex;
  }

  /**
   * Check if status is the next step (shows blue circle)
   * The step immediately after current status
   */
  function isNextStep(status: OrderStatus): boolean {
    const statusIndex = STATUS_FLOW.indexOf(status);
    return statusIndex === currentStatusIndex + 1;
  }

  /**
   * Check if status is current (for showing "Current" badge)
   */
  function isStatusCurrent(status: OrderStatus): boolean {
    return status === currentStatus;
  }

  /**
   * Get timeline entry for status
   */
  function getTimelineEntry(status: OrderStatus): StatusTimelineEntry | undefined {
    return timeline.find((entry) => entry.status === status);
  }

  return (
    <Card className="border-slate-200 bg-white shadow-md">
      <CardHeader className="pb-4 border-b border-slate-200">
        <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          Status Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          {STATUS_FLOW.map((status, index) => {
            const isCompleted = isStatusCompleted(status);
            const isCurrent = isStatusCurrent(status);
            const isNext = isNextStep(status);
            const entry = getTimelineEntry(status);
            const isLast = index === STATUS_FLOW.length - 1;

            return (
              <div key={status} className="relative">
                {/* Timeline connector line */}
                {!isLast && (
                  <div
                    className={`absolute left-5 top-10 w-0.5 h-12 ${
                      isCompleted ? 'bg-green-500' : 'bg-slate-200'
                    }`}
                  />
                )}

                <div className="flex items-start gap-4">
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <CheckCircle className="h-10 w-10 text-green-600 fill-green-50" />
                    ) : isNext ? (
                      <Circle className="h-10 w-10 text-blue-600 fill-blue-50" />
                    ) : (
                      <Circle className="h-10 w-10 text-slate-300" />
                    )}
                  </div>

                  {/* Status details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3
                        className={`text-sm font-semibold ${
                          isCompleted || isNext
                            ? 'text-slate-900'
                            : 'text-slate-400'
                        }`}
                      >
                        {entry?.statusLabel || STATUS_LABELS[status] || status.replace(/_/g, ' ')}
                      </h3>
                      {isCurrent && (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                          Current
                        </Badge>
                      )}
                      {isNext && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                          Next
                        </Badge>
                      )}
                    </div>

                    {entry && (
                      <div
                        className={`space-y-1 ${
                          isCompleted || isNext
                            ? 'opacity-100'
                            : 'opacity-50'
                        }`}
                      >
                        <p className="text-xs text-slate-600">
                          {formatDateTime(entry.timestamp)}
                        </p>
                        <p className="text-xs text-slate-500">
                          Updated by: {entry.updatedBy}
                        </p>
                        {entry.remarks && (
                          <p className="text-xs text-slate-600 italic">
                            {entry.remarks}
                          </p>
                        )}
                      </div>
                    )}

                    {!entry && !isCurrent && (
                      <p className={`text-xs ${isCompleted ? 'text-green-600' : isNext ? 'text-blue-600' : 'text-slate-400'}`}>
                        {isCompleted ? 'Completed' : isNext ? 'Up Next' : 'Pending'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Cancelled status (if applicable) */}
          {currentStatus === 'CANCELLED' && (
            <div className="relative pt-4 border-t border-red-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Circle className="h-10 w-10 text-red-600 fill-red-50" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-red-900">CANCELLED</h3>
                    <Badge variant="destructive" className="text-xs">
                      Cancelled
                    </Badge>
                  </div>
                  {getTimelineEntry('CANCELLED') && (
                    <div className="space-y-1">
                      <p className="text-xs text-red-700">
                        {formatDateTime(getTimelineEntry('CANCELLED')!.timestamp)}
                      </p>
                      <p className="text-xs text-red-600">
                        Cancelled by: {getTimelineEntry('CANCELLED')!.updatedBy}
                      </p>
                      {getTimelineEntry('CANCELLED')!.remarks && (
                        <p className="text-xs text-red-700 italic">
                          {getTimelineEntry('CANCELLED')!.remarks}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
